/**
 * /prd-template — SEO landing page targeting the "prd template" /
 * "prd template example" / "prd format" / "prd outline" cluster
 * (~1,500/mo; ChatPRD ranks here too).
 *
 * Differentiation angle: a PRD template in Notion or a Google Doc is a
 * fill-in-the-blanks outline — the moment you finish writing the product
 * requirements document, it starts going out of date, because nothing
 * connects a section to what depends on it. This page hands the reader the
 * actual template (the nine sections, spelled out, written to be usable as
 * a starter outline) so a "prd template" searcher gets the real thing,
 * then shows that Draftlize turns each section into a structured card
 * where dependents auto-flag stale when an upstream decision changes.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in PrdTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function PrdTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/prd-template"
      documentTitle="PRD Template That Doesn't Drift · Draftlize"
      documentDescription="A PRD template you can copy — Problem, Goals, Non-goals, Target users, User stories, Functional requirements, Success metrics, Dependencies, Open questions. The catch: a PRD template in Notion or a Google Doc is stale the moment you finish filling it in. In Draftlize every section is a structured card, and dependents auto-flag stale when an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · PRDs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A PRD template<br/>
            <em>that doesn't drift.</em>
          </h1>
          <p className="lede">
            You came for a <em>PRD template</em> — the sections, the headings, an outline to paste into a doc and start a <a href="/product-requirements-document" style={{ color: 'var(--amber)' }}>product requirements document</a>. It's below; take it, it's a real one. But know what you're getting: a template is a fill-in-the-blanks outline, and the instant you finish filling it in, it begins to drift. A Notion page or a Google Doc has no idea when a goal you wrote stops matching the decision underneath it, so the PRD quietly diverges from what the team is actually building. Draftlize gives you the same nine sections — and makes each one a card an AI agent reads and writes, where everything downstream flags itself stale the moment one decision moves.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Nine sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole PRD format. It works in a doc, a wiki, or a markdown file — paste it and fill it in. Use it as-is. The next section is about what changes when each heading stops being prose and becomes a card.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '1 · Problem statement',
              hint: 'Required',
              body: 'The user pain in one or two sentences — what hurts, for whom, and why it matters now. If you can\'t name the problem without naming your solution, you don\'t have a problem statement yet; you have a feature looking for a reason.',
            },
            {
              k: '2 · Goals',
              hint: 'Outcomes',
              body: 'What success looks like, stated as outcomes, not features. "Cut time-to-first-draft below five minutes," not "add a templates button." Three at most — a PRD with nine goals has none.',
            },
            {
              k: '3 · Non-goals',
              hint: 'Out of scope',
              body: 'What you are deliberately not doing in this release, written down so it doesn\'t get re-debated in week three. The most under-used section in any PRD template, and the one that saves the most arguments.',
            },
            {
              k: '4 · Target users',
              hint: 'Who',
              body: 'The specific person this is for — role, context, what they\'re trying to get done. "Solo developers shipping a side project," not "users." Everything downstream, from stories to metrics, anchors to this.',
            },
            {
              k: '5 · User stories',
              hint: 'Jobs',
              body: 'As a [user], I want [capability], so that [outcome]. The bridge from the problem to the requirements — each story is a job the product has to do, in the user\'s words, not the system\'s.',
            },
            {
              k: '6 · Functional requirements',
              hint: 'What it must do',
              body: 'The concrete behaviors the product must support, numbered so they\'re citable. This is the longest section and the one engineering actually builds from. Each requirement should trace back to a story and forward to a metric.',
            },
            {
              k: '7 · Success metrics',
              hint: 'How you\'ll know',
              body: 'The numbers that tell you the goals were met — activation, retention, the specific funnel step that moves. Decide them before you ship, or you\'ll rationalize whatever happened into a win after.',
            },
            {
              k: '8 · Dependencies',
              hint: 'What this rests on',
              body: 'The decisions, systems, and other work this PRD assumes are true — pricing, the auth model, an upstream API. In a doc this is a list you\'ll forget to update. It\'s the section that decides whether the PRD stays honest.',
            },
            {
              k: '9 · Open questions',
              hint: 'Unresolved',
              body: 'What you don\'t know yet, named out loud rather than buried. A PRD that pretends to have every answer is fiction; the open-questions list is where the real work of the next two weeks lives.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why filling it in is the moment it starts to drift — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a PRD template <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A template is an outline, not a system</h3>
            <p>The nine sections are good — that's roughly the shape every PRD format has converged on. But an outline only captures the product once. It has no idea when a goal stops matching the decision beneath it, because a page of prose can't watch the rest of the project change around it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The Dependencies section is a promise you'll break</h3>
            <p>You list what the PRD rests on — pricing, the auth model, a decision made last sprint. Then one of those changes, and reconciling the doc is manual archaeology: re-read every requirement to find the ones that assumed the old call. Nobody does it, so the section meant to keep the PRD honest is the first to go stale.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Filled in is the high-water mark</h3>
            <p>The PRD is most accurate the minute after you finish it, and never that accurate again. By the time engineering is mid-build, a real fraction of it is wrong, and you can't tell which fraction — so the team stops trusting the doc and starts asking in Slack, which is the failure the PRD was supposed to prevent.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section becomes a card, not a heading</h3>
            <p>In Draftlize the PRD isn't a blank doc — it's a set of typed cards along those exact sections: problem, goals, non-goals, users, stories, requirements, metrics, dependencies, open questions. Structured, addressable by ID, and citable from a spec or a thread instead of buried under a heading three scrolls down.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Because Dependencies is a real link and not a list of nouns, changing a decision turns every card that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. Change the pricing decision and the requirements and metrics that assumed it light up, instead of silently lying. The promise the template made, the substrate keeps.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant card before it drafts the next requirement and writes new sections back into the same shape. The PRD stops being a document for a reader who skims it once and becomes context that gets read on every turn — pairs naturally with a <a href="/decision-log-template" style={{ color: 'var(--amber)' }}>decision log template</a> for the calls underneath it.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A template tells you what sections to write. It can't tell you when a section stopped matching the decision underneath it.
        </blockquote>
        <cite>Keep the nine sections. Let the substrate keep them current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What should a PRD template include?', a: 'At minimum: problem statement, goals, non-goals, target users, user stories, functional requirements, success metrics, dependencies, and open questions. Those nine sections are the shape most PRD formats have converged on — skip a heading if you don\'t need it, but answer the underlying question.' },
            { q: 'What is the difference between a PRD template and a PRD example?', a: 'A template is the blank outline — the section headings you fill in. An example is a template already filled in for a specific product, so you can see the level of detail expected. Use the template to write; read an example to calibrate.' },
            { q: 'Can I use ChatGPT or an AI to fill in a PRD template?', a: 'Yes — an AI writer turns a rough brief into a solid first draft fast. The gap is durability: it drafts the PRD once and has no idea when a decision it assumed later changes. That is the part a template plus an AI writer can\'t solve, and it\'s why the sections drift.' },
            { q: 'How is a Draftlize PRD template different from one in Notion or Google Docs?', a: 'A Notion or Docs template is a static outline — the instant you finish filling it in, it starts going stale. Draftlize gives you the same nine sections as typed cards: each is addressable by ID, an AI agent reads and writes them over MCP, and when one decision changes every dependent section auto-flags stale instead of silently lying.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your PRD <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent draft the product requirements document from a rough brief — then keep every section, and every decision it depends on, current for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="prd" />
    </MarketingShell>
  );
}
